import { useCallback, useMemo, useState, type FC, type ReactNode } from 'react'
import { useFormikContext } from 'formik'
import { useParams } from 'react-router-dom'
import { Calendar, DollarSign, FileText, Handshake, Users, Activity } from 'lucide-react'
import { KTIcon } from '../../../../../_theme/helpers'
import { useSnackbar } from '../../../../../_theme/context/SnackbarContext'
import { CurrentStageStep, Stagestep } from '../../core/_models'
import { updateElevatorPitchFields, updateElevatorPitchTimeline } from '../../core/_request'
import StepIconSidebar from '../../Stage/StepIconSidebar'
import DynamicSymbol from '../../../apps/components/DynamicSymbol'
import { MyEditor } from '../../../apps/components/MyEditor'
import { IElevatorPitch, IElevatorPitchStakeholder } from '../../../core/_models'
import { getApiErrorMessage } from '../../../../../utils/utils'
import { normalizeRichTextValue } from '../../../../../utils/sanitizeRichText'
import { IdentifyKeyStakeholders } from './IdentifyKeyStakeholders'
import { PrivateSectorOpportunities } from './PrivateSectorOpportunities'
import { ProjectCostSize } from './ProjectCostSize'
import { ProjectStatus } from './ProjectStatus'
import { ProjectSummaryTabs } from './ProjectSummaryTabs'
import { ProjectTimeline } from './ProjectTimeline'
import { TimelineTable } from './TimelineTable'

type Props = {
    values: IElevatorPitch
    stakeholders: IElevatorPitchStakeholder[]
    submitLoader: boolean
    disableFormFields: boolean
}

type ElevatorStep = Stagestep & {
    content: ReactNode
}

const ObjectivesPreview: FC<{ objectives: IElevatorPitch['project_objectives_areas'] }> = ({ objectives }) => {

    const rows = useMemo(() => {
        return objectives?.filter((row) => row?.delete !== 'Yes') ?? []
    }, [objectives])

    
    return (
        <div className="table-responsive mb-10">
            <table className="table table-bordered border-secondary w-100">
                <thead>
                    <tr className="bg-secondary align-middle">
                        <th className="text-gray-700">#</th>
                        <th className="text-gray-700">Objective</th>
                        <th className="text-gray-700">Key Focus Area</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 ? (
                        <tr>
                            <td colSpan={3} className="text-center">No data added yet.</td>
                        </tr>
                    ) : (
                        rows.map((row, index) => (
                            <tr key={row.row_id ?? index}>
                                <td>{index + 1}</td>
                                <td>{row.objective_name}</td>
                                <td>{row.keyFocus_name?.join(', ')}</td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    )
}

const RichTextPreview: FC<{ html: string }> = ({ html }) => {
    const previewHtml = normalizeRichTextValue(html || '')

    if (!previewHtml) {
        return <div className="mb-10 rounded border bg-light p-4 text-muted">No overview has been added yet.</div>
    }

    return (
        <div
            className="mb-10 rounded border bg-light p-4 text-gray-800"
            dangerouslySetInnerHTML={{ __html: previewHtml }}
        />
    )
}

const fieldsForStep = (stepKey: string, values: IElevatorPitch): Record<string, string> | null => {
    switch (stepKey) {
        case 'project_summary':
            return {
                elevator_project_overview: values.updated_background_justification,
                key_objectives_summary_editor: values.key_objectives_summary_editor,
            }
        case 'project_status':
            return { project_status: values.project_status }
        case 'financial_needs':
            return { financial_needs: values.financial_needs.project_cost_editor }
        case 'private_sector_opportunities':
            return { private_sector_opportunities: values.private_sector_opportunities }
        default:
            return null
    }
}

export const ElevatorPitch: FC<Props> = ({ values, stakeholders, submitLoader, disableFormFields }) => {
    const { project_id } = useParams()
    const { showSnackbar } = useSnackbar()
    const [activeStep, setActiveStep] = useState<number>(0)
    const [savingAction, setSavingAction] = useState<'save' | 'save-next' | null>(null)
    const { setFieldValue } = useFormikContext()

    const steps = useMemo<ElevatorStep[]>(() => [
        {
            key: 'project_summary',
            title: 'Project Summary',
            description: 'High-Level project overview and objectives',
            icon: <FileText size={22} />,
            iconColor: 'text-primary',
            iconBackground: 'bg-light-primary',
            showIndex: false,
            content: (
                <ProjectSummaryTabs
                    tabs={[
                        {
                            key: 'background',
                            label: 'High-Level Project Overview',
                            content: (
                                <div>
                                    <RichTextPreview html={values.background_justification} />
                                    <hr />
                                    <h4 className="mb-4">High-Level Project Overview Summary</h4>
                                    <MyEditor disabled={disableFormFields} value={values.updated_background_justification} setValue={(value: string) => {
                                        setFieldValue('updated_background_justification', value)
                                    }} />
                                </div>
                            ),
                        },
                        {
                            key: 'objectives',
                            label: 'Project Objectives & Focus Areas',
                            content: (
                                <div>
                                    <ObjectivesPreview objectives={values.project_objectives_areas} />
                                    <hr />
                                    <h4 className="mb-4">Project Objectives Summary</h4>
                                    <MyEditor disabled={disableFormFields} value={values.key_objectives_summary_editor} setValue={(value: string) => {
                                        setFieldValue('key_objectives_summary_editor', value)
                                    }} />
                                </div>
                            ),
                        },
                    ]}
                    defaultActiveKey="background"
                />
            ),
        },
        {
            key: 'project_status',
            title: 'Project Status',
            description: 'Current status of the project',
            icon: <Activity size={22} />,
            iconColor: 'text-success',
            iconBackground: 'bg-light-success',
            showIndex: false,
            content: <ProjectStatus values={values} submitLoader={submitLoader} disableFormFields={disableFormFields} />,
        },
        {
            key: 'financial_needs',
            title: 'Financial Needs',
            description: 'Estimated cost, project type, and size',
            icon: <DollarSign size={22} />,
            iconColor: 'text-warning',
            iconBackground: 'bg-light-warning',
            showIndex: false,
            content: <ProjectCostSize values={values} submitLoader={submitLoader} disableFormFields={disableFormFields} />,
        },
        {
            key: 'private_sector_opportunities',
            title: 'Private Sector Opportunities',
            description: 'Opportunities for private sector participation',
            icon: <Handshake size={22} />,
            iconColor: 'text-info',
            iconBackground: 'bg-light-info',
            showIndex: false,
            content: <PrivateSectorOpportunities values={values} submitLoader={submitLoader} disableFormFields={disableFormFields} />,
        },
        {
            key: 'key_parties',
            title: 'Key Parties',
            description: 'Government, state-owned, private, and other stakeholders',
            icon: <Users size={22} />,
            iconColor: 'text-danger',
            iconBackground: 'bg-light-danger',
            showIndex: false,
            content: <IdentifyKeyStakeholders submitLoader={submitLoader} stakeholders={stakeholders} />,
        },
        {
            key: 'timeline',
            title: 'Timeline',
            description: 'Estimated duration, start date, and milestones',
            icon: <Calendar size={22} />,
            iconColor: 'text-primary',
            iconBackground: 'bg-light-primary',
            showIndex: false,
            content: (
                <div>
                    {/* <ProjectTimeline values={values} submitLoader={submitLoader} /> */}
                    <TimelineTable timeline_table_areas={values.timeline.timeline_table_areas} submitLoader={submitLoader} disableFormFields={disableFormFields} />
                </div>
            ),
        },
    ], [disableFormFields, setFieldValue, stakeholders, submitLoader, values])

    const current = steps[activeStep] ?? steps[0]

    const currentStep: CurrentStageStep = {
        index: activeStep,
        subIndex: 0,
        step: current,
        isFirst: activeStep === 0,
        isLast: activeStep === steps.length - 1,
    }

    const handleStepClick = useCallback((key: string) => {
        const index = steps.findIndex((step) => step.key === key)
        if (index >= 0) {
            setActiveStep(index)
        }
    }, [steps])

    const saveCurrentStep = useCallback(async (stepKey: string): Promise<boolean> => {
        if (!project_id || stepKey === 'key_parties') {
            return true
        }

        try {
            const response = stepKey === 'timeline'
                ? await updateElevatorPitchTimeline({
                    project_id,
                    timeline: values.timeline.timeline_table_areas ?? [],
                })
                : await updateElevatorPitchFields({
                    project_id,
                    ...(fieldsForStep(stepKey, values) ?? {}),
                })

            if (response.status === 'ok') {
                showSnackbar(response.message, 'success')
                return true
            }

            showSnackbar(response.message, 'danger')
            return false
        } catch (error) {
            showSnackbar(getApiErrorMessage(error, 'Something went wrong'), 'danger')
            return false
        }
    }, [project_id, showSnackbar, values])

    const handleSave = async (advance: boolean) => {
        if (savingAction) return
        setSavingAction(advance ? 'save-next' : 'save')
        const saved = await saveCurrentStep(current.key)
        setSavingAction(null)
        if (saved && advance) {
            setActiveStep((step) => step + 1)
        }
    }

    return (
        <div
            className="stepper stepper-pills stepper-column d-flex flex-column flex-xl-row flex-row-fluid"
            id="elevator_stepper"
        >
            <StepIconSidebar
                steps={steps}
                currentStep={currentStep}
                onStepClick={handleStepClick}
            />
            <div className="d-flex flex-row-fluid justify-content-center bg-body rounded">
                <div className="p-8 w-100 position-relative">
                    <div className="card card-custom">
                        <div className="card-header align-items-center">
                            <div className="d-flex align-items-center">
                                <DynamicSymbol
                                    size={40}
                                    bgClass={`${current.iconColor} ${current.iconBackground}`}
                                    icon={current.icon}
                                />
                                <div>
                                    <h3 className="card-title align-items-center mb-0 mt-0">{current.title}</h3>
                                    {current.description && <div className="text-muted fs-7 mt-0">{current.description}</div>}
                                </div>
                            </div>
                        </div>
                        <div className="card-body pt-2 px-10 pb-8">
                            {current.content}
                            {!disableFormFields && (
                                <div className="d-flex flex-stack pt-6 pb-0 px-4">
                                    <div className="mr-2">
                                        {activeStep > 0 && (
                                            <button
                                                type="button"
                                                className="btn btn-md btn-light-primary me-3"
                                                onClick={() => setActiveStep((step) => step - 1)}
                                            >
                                                <KTIcon iconName="arrow-left" className="fs-4 me-1" />
                                                Back
                                            </button>
                                        )}
                                    </div>
                                    <div>
                                        <button
                                            type="button"
                                            className="btn btn-md btn-primary"
                                            disabled={savingAction !== null}
                                            onClick={() => handleSave(false)}
                                        >
                                            {savingAction === 'save' && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>}
                                             { activeStep < steps.length - 1 ? "Save" : "Submit"}
                                        </button>
                                        {activeStep < steps.length - 1 && (
                                            <button
                                                type="button"
                                                className="btn btn-md btn-primary ms-3"
                                                disabled={savingAction !== null}
                                                onClick={() => handleSave(true)}
                                            >
                                                <span className="indicator-label d-flex flex-center">
                                                    {savingAction === 'save-next' && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>}
                                                    Save and Next
                                                    <KTIcon iconName="arrow-right" className="fs-3 ms-2 me-0" />
                                                </span>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
